import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import axios from "axios";
import Cookies from "js-cookie";

import logo1 from "../../public/1.webp";
import logo2 from "../../public/2.webp";
import logo3 from "../../public/4.webp";

const insertSpacesInCamelCase = (str = "") =>
  String(str)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/&/g, " & ")
    .trim();

const tierLogos = {
  ugfree: logo1,
  ugbasic: logo2,
  ugpremium: logo3,
};

const checkIcon = <span className="text-green-500 text-[15px]">✔</span>;

export default function Plans() {
  const router = useRouter();
  const [remotePricing, setRemotePricing] = useState(null);
  const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
  const pricingKeyMap = {
    ugbasic: "basic",
    ugpremium: "premium",
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        if (!API_URL) return;
        const response = await axios.get(`${API_URL}/api/fetch-services-data`);

        if (response?.data?.data?.length > 0) {
          const fetched = response.data.data[0];
          const copy = JSON.parse(JSON.stringify(fetched));
          delete copy._id;
          if (copy.priceDetails) delete copy.priceDetails._id;
          if (copy.features) delete copy.features._id;
          setRemotePricing(copy);
        }
      } catch (err) {
        console.error("Failed to fetch UG pricing for Plans:", err);
      }
    };

    fetchPricing();
  }, [API_URL]);

  const handleCheckout = (tier) => {
    const priceFromApi = remotePricing?.priceDetails?.[pricingKeyMap[tier] || tier];
    const price = typeof priceFromApi !== "undefined" ? priceFromApi : 0;

    const featuresArray = featureRows
      .filter(row => row[tier] === "Yes")
      .map(row => row.feature);

    const authToken = Cookies.get("authToken");
    const isLoggedIn = authToken && authToken !== "undefined" && authToken !== "null" && authToken.trim() !== "";

    const checkoutQuery = `tier=${encodeURIComponent(tier)}&price=${encodeURIComponent(price)}&features=${encodeURIComponent(JSON.stringify(featuresArray))}`;
    const checkoutPath = `/checkout?${checkoutQuery}`;

    if (!isLoggedIn) {
      try { sessionStorage.setItem('checkoutAfterLogin', JSON.stringify({ tier, price, features: featuresArray })); } catch (e) {}
      const loginHost = 'https://portal.neetsupport.com';
      const returnUrl = window.location.origin + checkoutPath;
      const loginUrl = `${loginHost}/login?next=${encodeURIComponent(returnUrl)}`;
      window.location.href = loginUrl;
      return;
    }

    router.push({ pathname: '/checkout', query: { tier, price, features: JSON.stringify(featuresArray) } });
  };

  const priceBasic = remotePricing?.priceDetails?.basic;
  const pricePremium = remotePricing?.priceDetails?.premium;

  const featureRows = useMemo(() => {
    if (!remotePricing || !remotePricing.features) return [];
    const sectionOrder = ["tools", "analysis", "postExamMentorship"];
    const allSections = Object.keys(remotePricing.features).filter((s) => s !== "lastUpdatedBy" && s !== "_id");
    const orderedSections = [
      ...sectionOrder.filter(s => allSections.includes(s)),
      ...allSections.filter(s => !sectionOrder.includes(s))
    ];

    const rows = [];
    orderedSections.forEach((section) => {
      const sectionFeatures = remotePricing.features[section] || {};
      Object.keys(sectionFeatures).forEach((featureKey) => {
        const freeVal = sectionFeatures[featureKey]["free"];
        const basicVal = sectionFeatures[featureKey]["basic"];
        const premiumVal = sectionFeatures[featureKey]["premium"];

        rows.push({
          section: insertSpacesInCamelCase(section),
          feature: insertSpacesInCamelCase(featureKey),
          ugfree: freeVal,
          ugbasic: basicVal,
          ugpremium: premiumVal,
        });
      });
    });

    return rows;
  }, [remotePricing]);

  const groupedFeatures = useMemo(() => {
    const grouped = {};
    featureRows.forEach((row) => {
      if (!grouped[row.section]) grouped[row.section] = [];
      grouped[row.section].push(row);
    });
    return grouped;
  }, [featureRows]);

  if (!remotePricing) {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading pricing information...</div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Choose What Best <span className="text-[#ff9245]">For You</span></h2>
        <p className="text-sm sm:text-lg text-gray-600">Plans for every NEET UG Counselling aspirant—select the right one and take a step toward your dream seat.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden shadow-xl" style={{ background: 'linear-gradient(to bottom, #e8f5f4 0%, #d4eeec 100%)' }}>
          <div className="p-8">
            <div className="flex flex-col items-center justify-center mb-6">
              <Image src={tierLogos.ugbasic} alt="basic logo" width={48} height={48} className="mb-3" sizes="48px" priority={true} fetchPriority="high" />
              <h3 className="text-3xl font-bold" style={{ color: '#0e605b' }}>Basic</h3>
            </div>

            <div className="mb-4 text-center">
              <div className="flex items-baseline gap-2 justify-center">
                <span className="text-xl font-semibold text-gray-400 line-through mr-2">₹4999</span>
                <span className="text-5xl font-bold" style={{ color: '#0e605b' }}>₹{priceBasic}</span>
                <span className="text-sm text-gray-600">+GST</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <h4 className="text-sm font-bold text-gray-700 mb-4">KEY FEATURES</h4>
              <div className="space-y-3">
                {Object.keys(groupedFeatures).map((sectionName) =>
                  groupedFeatures[sectionName]
                    .filter((row) => row.ugbasic === "Yes")
                    .map((row, idx) => (
                      <div key={`basic-${sectionName}-${idx}`} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5" style={{ backgroundColor: '#0e605b' }}>
                          {checkIcon}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{row.feature}</p>
                      </div>
                    ))
                )}
              </div>
            </div>

            <button onClick={() => handleCheckout("ugbasic")} className="w-full text-white font-semibold py-4 px-6 rounded-full transition duration-300 shadow-md" style={{ backgroundColor: '#0e605b' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#0b4d4a'} onMouseLeave={(e) => e.target.style.backgroundColor = '#0e605b'}>Purchase now</button>
          </div>
        </div>

        <div className="relative rounded-3xl overflow-hidden shadow-xl" style={{ background: 'linear-gradient(to bottom, #FFF5F0 0%, #FFEEE5 100%)' }}>
          <div className="p-6">
            <div className="flex flex-col items-center justify-center mb-6">
              <Image src={tierLogos.ugpremium} alt="premium logo" width={48} height={48} className="mb-3" sizes="48px" priority={true} fetchPriority="high" />
              <h3 className="text-2xl font-bold text-orange-500">Premium</h3>
            </div>

            <div className="mb-4 text-center">
              <div className="flex items-baseline gap-2 justify-center">
                <span className="text-4xl font-bold text-orange-500">₹{pricePremium}</span>
                <span className="text-xs text-gray-600">+GST</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm min-h-[300px]">
              <h4 className="text-xs font-bold text-gray-700 mb-3">KEY FEATURES</h4>
              <div className="space-y-2">
                {Object.keys(groupedFeatures).map((sectionName) =>
                  groupedFeatures[sectionName]
                    .filter((row) => row.ugpremium === "Yes")
                    .map((row, idx) => (
                      <div key={`premium-${sectionName}-${idx}`} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5" style={{ backgroundColor: '#ff9245' }}>
                          {checkIcon}
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">{row.feature}</p>
                      </div>
                    ))
                )}
              </div>
            </div>

            <button onClick={() => handleCheckout("ugpremium")} className="w-full bg-[#ff9245] hover:bg-[#fb923c] text-white font-semibold py-3 px-4 rounded-full transition duration-300 shadow-md text-sm">Purchase now</button>
          </div>
        </div>
      </div>
    </section>
  );
}
